import {
  criarCondicaoPagamentoSchema,
  gerarParcelasSchema,
  percentualParaTaxa,
  type CondicaoPagamentoResponse,
  type CondicaoTipo,
  type CriarCondicaoPagamentoDto,
  type GerarParcelasDto,
  type RegenerarCondicaoDto,
} from '@triade/contracts';
import { decimalParaTextoCampo, fracaoParaPercentualTexto } from '@/lib/format';

/**
 * Entrada do formulario. Nada aqui calcula parcela, prazo ou percentual: converte texto digitado no formato que a API
 * recebe (`percentualParaTaxa`, validacao Zod do contrato) e mapeia a resposta. A grade mostrada vem de /gerar ou do detalhe.
 */
export interface CondicaoFormEntrada {
  codigo: string;
  nome: string;
  ativa: boolean;
  /** '' = condicao importada sem tipo (so vira gerada ao escolher um tipo e Regenerar). */
  tipo: CondicaoTipo | '';
  quantidade: string;
  intervaloDias: string;
  comEntrada: boolean;
  /** Texto pt-BR do percentual ("10", "12,5"). */
  entradaPercentual: string;
  /** Linhas da grade manual: dias e percentual em texto. */
  parcelas: { days: string; pct: string }[];
}

export const TIPO_ROTULO: Record<CondicaoTipo, string> = {
  A_VISTA: 'À vista',
  PARCELAS_IGUAIS: 'Parcelas iguais',
  MANUAL: 'Manual',
  ENTRADA_MANUAL_IGUAIS: 'Entrada manual + iguais',
};

/** Tipos que a API gera (a grade nunca e editada na tela). */
export const TIPOS_GERADOS = ['A_VISTA', 'PARCELAS_IGUAIS', 'ENTRADA_MANUAL_IGUAIS'] as const;

export const CONDICAO_VAZIA: CondicaoFormEntrada = {
  codigo: '',
  nome: '',
  ativa: true,
  tipo: 'PARCELAS_IGUAIS',
  quantidade: '1',
  intervaloDias: '30',
  comEntrada: false,
  entradaPercentual: '',
  parcelas: [{ days: '0', pct: '100' }],
};

/** Texto de campo inteiro -> numero (NaN se nao for inteiro: o Zod do contrato recusa). */
const inteiro = (texto: string): number => (/^\d+$/.test(texto.trim()) ? Number(texto.trim()) : Number.NaN);

/**
 * Parametros de geracao a partir do formulario (ou null se faltar algo). Entrada manual + iguais tem entrada sempre.
 * A quantidade conta a entrada ("Entrada + 9" = 10).
 */
export function paramsDeGeracao(v: CondicaoFormEntrada): GerarParcelasDto | null {
  if (v.tipo === '' || v.tipo === 'MANUAL') return null;
  if (v.tipo === 'A_VISTA') return { tipo: 'A_VISTA', quantidade: 1, intervaloDias: 0, comEntrada: false };
  const ehEntradaManual = v.tipo === 'ENTRADA_MANUAL_IGUAIS';
  const entradaPercentual = ehEntradaManual ? percentualParaTaxa(v.entradaPercentual) : null;
  if (ehEntradaManual && entradaPercentual === null) return null;
  const r = gerarParcelasSchema.safeParse({
    tipo: v.tipo,
    quantidade: inteiro(v.quantidade),
    intervaloDias: inteiro(v.intervaloDias),
    comEntrada: ehEntradaManual ? true : v.comEntrada,
    ...(entradaPercentual !== null ? { entradaPercentual } : {}),
  });
  return r.success ? r.data : null;
}

/** Chave estavel dos parametros (para debounce e comparacao com o gravado). */
export const chaveParams = (p: GerarParcelasDto | null): string => (p ? JSON.stringify(p) : '');

/** Condicao gravada -> valores do formulario (so mapeia; percentual de fracao da API para texto). */
export function deCondicao(c: CondicaoPagamentoResponse): CondicaoFormEntrada {
  return {
    codigo: c.codigo,
    nome: c.nome,
    ativa: c.ativa,
    tipo: c.tipo ?? '',
    quantidade: c.quantidade !== null ? String(c.quantidade) : '1',
    intervaloDias: c.intervaloDias !== null ? String(c.intervaloDias) : '30',
    comEntrada: c.comEntrada,
    entradaPercentual: fracaoParaPercentualTexto(c.entradaPercentual),
    parcelas: (c.parcelas ?? []).map((p) => ({ days: decimalParaTextoCampo(p.days), pct: fracaoParaPercentualTexto(p.pct) })),
  };
}

/** Linhas da grade gravada (detalhe) para exibir como vieram. */
export const gradeGravada = (c: CondicaoPagamentoResponse | undefined): { days: string; pct: string }[] | undefined =>
  c?.parcelas?.map((p) => ({ days: p.days, pct: p.pct }));

export type Resultado<T> = { ok: true; dados: T } | { ok: false; mensagem: string };

/** Corpo do POST: nome/codigo validados pelo contrato. MANUAL leva as linhas digitadas; os gerados levam so os parametros. */
export function paraCriar(v: CondicaoFormEntrada): Resultado<CriarCondicaoPagamentoDto> {
  if (v.tipo === '') return { ok: false, mensagem: 'Escolha o tipo da condição' };
  let corpo: Record<string, unknown> = { codigo: v.codigo, nome: v.nome, ativa: v.ativa, parcelas: [] };
  if (v.tipo === 'MANUAL') {
    const linhas = v.parcelas.map((p) => ({ days: p.days.trim(), pct: percentualParaTaxa(p.pct) }));
    if (linhas.some((l) => l.pct === null)) return { ok: false, mensagem: 'Percentual inválido na grade (0 a 100, até 6 casas)' };
    corpo = { ...corpo, tipo: 'MANUAL', parcelas: linhas };
  } else {
    const p = paramsDeGeracao(v);
    if (!p) return { ok: false, mensagem: 'Confira quantidade, intervalo e entrada' };
    corpo = { ...corpo, ...p };
  }
  const r = criarCondicaoPagamentoSchema.safeParse(corpo);
  return r.success ? { ok: true, dados: r.data } : { ok: false, mensagem: r.error.issues[0]?.message ?? 'Dados inválidos' };
}

/** Corpo do Regenerar: os parametros de geracao (so tipos gerados). */
export function paraRegenerar(v: CondicaoFormEntrada): Resultado<RegenerarCondicaoDto> {
  if (v.tipo === '') return { ok: false, mensagem: 'Escolha o tipo antes de regenerar' };
  const p = paramsDeGeracao(v);
  if (!p) return { ok: false, mensagem: 'Confira quantidade, intervalo e entrada' };
  return { ok: true, dados: p };
}
